import { trackEvent } from '@/utils/trackingManager';
import { shouldSuppressSensitiveAuthTelemetry } from '@/utils/urlPrivacy';

const KEY = 'jp_google_signin_attempt';
const DEFERRED_KEY = 'jp_google_signin_outcomes';
const CALLBACK_PARAM = 'google_attempt';
const MAX_AGE_MS = 15 * 60 * 1000;
type Outcome = 'launch_requested' | 'browser_opened' | 'fallback_started' | 'cancelled' | 'completed' | 'failed' | 'returned_without_session' | 'timed_out';
type Stage = 'native_sdk' | 'browser' | 'session_exchange' | 'callback' | 'start';
type Attempt = { id: string; startedAt: number };
type Deferred = Attempt & { outcome: Outcome; stage: Stage };
const OUTCOMES = new Set(['launch_requested', 'browser_opened', 'fallback_started', 'cancelled', 'completed', 'failed', 'returned_without_session', 'timed_out']);
const STAGES = new Set(['native_sdk', 'browser', 'session_exchange', 'callback', 'start']);
const TERMINAL = new Set<Outcome>(['completed', 'cancelled', 'failed']);
let memory: Attempt | null = null;
let deferredMemory: Deferred[] = [];
let storageFailed = false;
let deferredStorageFailed = false;

function isAttempt(value: unknown): value is Attempt {
  if (!value || typeof value !== 'object') return false;
  const item = value as Partial<Attempt>;
  return typeof item.id === 'string' && item.id.length > 0 && item.id.length <= 100 &&
    typeof item.startedAt === 'number' && Number.isFinite(item.startedAt) &&
    Date.now() - item.startedAt <= MAX_AGE_MS && item.startedAt <= Date.now();
}

function read(): Attempt | null {
  try {
    const stored = window.sessionStorage.getItem(KEY);
    if (!storageFailed) memory = stored ? JSON.parse(stored) as Attempt : null;
  } catch { /* In-memory tracking still works when storage is unavailable. */ }
  return isAttempt(memory) ? memory : null;
}

function readDeferred(): Deferred[] {
  try {
    const stored = window.sessionStorage.getItem(DEFERRED_KEY);
    if (!deferredStorageFailed) {
      const value: unknown = stored ? JSON.parse(stored) : [];
      deferredMemory = Array.isArray(value) ? value.filter((event): event is Deferred =>
        isAttempt(event) && 'outcome' in event && typeof event.outcome === 'string' &&
        OUTCOMES.has(event.outcome) && 'stage' in event && typeof event.stage === 'string' && STAGES.has(event.stage)).slice(-16) : [];
    }
  } catch { /* Keep the in-memory queue when storage is unavailable. */ }
  return deferredMemory.filter(isAttempt);
}

function saveDeferred(events: Deferred[]): void {
  deferredMemory = events;
  try {
    if (events.length) window.sessionStorage.setItem(DEFERRED_KEY, JSON.stringify(events));
    else window.sessionStorage.removeItem(DEFERRED_KEY);
  } catch { deferredStorageFailed = true; }
}

export function beginGoogleSignInAttempt(): string {
  flushGoogleSignInOutcomes();
  memory = {
    id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    startedAt: Date.now(),
  };
  try { window.sessionStorage.setItem(KEY, JSON.stringify(memory)); } catch { storageFailed = true; }
  return memory.id;
}

export function getGoogleSignInAttemptId(): string | undefined {
  return read()?.id;
}

export function withGoogleSignInAttempt(url: string, attemptId: string | undefined): string {
  if (!attemptId) return url;
  const callback = new URL(url);
  callback.searchParams.set(CALLBACK_PARAM, attemptId);
  return callback.toString();
}

/** Callback correlation never authorizes authentication and does not depend on account-origin metadata. */
export function getGoogleCallbackAttemptId(params: URLSearchParams): string | undefined {
  const id = params.get(CALLBACK_PARAM);
  return id && id === getGoogleSignInAttemptId() ? id : undefined;
}

export function clearGoogleSignInAttempt(): void {
  memory = null;
  try { window.sessionStorage.removeItem(KEY); } catch { storageFailed = true; }
}

function send(event: Deferred): void {
  // Existing consent and destination gates remain in control of delivery.
  try {
    void trackEvent('google_signin_outcome', {
      outcome: event.outcome, stage: event.stage, attempt_id: event.id,
    }).catch(() => undefined);
  } catch { /* Analytics must never affect sign-in. */ }
}

/** A credential-bearing document stays suppressed; a fresh safe document can replay the bounded queue. */
export function flushGoogleSignInOutcomes(): void {
  if (shouldSuppressSensitiveAuthTelemetry()) return;
  const events = readDeferred();
  saveDeferred([]);
  for (const event of events) send(event);
}

/** Only fixed enums and a random per-attempt ID leave this module. No provider errors or credentials. */
export function trackGoogleSignInOutcome(outcome: Outcome, stage: Stage, attemptId: string | undefined): void {
  const attempt = read();
  if (!attempt || attempt.id !== attemptId) return;
  const event = { id: attempt.id, startedAt: attempt.startedAt, outcome, stage };
  if (shouldSuppressSensitiveAuthTelemetry()) {
    saveDeferred([...readDeferred(), event].slice(-16));
    if (TERMINAL.has(outcome)) clearGoogleSignInAttempt();
    return;
  }
  flushGoogleSignInOutcomes();
  if (TERMINAL.has(outcome)) clearGoogleSignInAttempt();
  send(event);
}
